import i18n from "@/i18n";
/**
 * MultiShiftEditor — Multi-Shift Editor Core (12I-1A) +
 * State Hardening / Reconciliation (12I-1B).
 *
 * Local reusable core for managing independently-addressable planned
 * Shift blocks belonging to exactly ONE employee + ONE Schedule date.
 *
 * 12I-1A behavior preserved (§7 of 12I-1B):
 *   • controlled Popover, Manage-shifts header, Employee/date context;
 *   • one row per existing Shift in backend order (identity via
 *     schedule_entry_id);
 *   • local Start/End editing with `parseQuickShiftInput` reuse;
 *   • update intent (mode=update, source=multi-editor);
 *   • targeted delete intent;
 *   • + Add shift with ONE unsaved draft maximum;
 *   • create intent (mode=create, source=multi-editor);
 *   • Discard, close/reopen reset, N/A restrictions;
 *   • no local interval-collision calculation, no API.
 *
 * 12I-1B adds:
 *   • cell-level pending lock via `isPending` prop;
 *   • active mutation row + local "Saving…" indicator (§11);
 *   • all mutation-producing actions disabled while pending (§15);
 *   • canonical existing-row sync by schedule_entry_id (§22 · §38 ·
 *     §39) — canonical Shift object replaces stale local refs;
 *   • successful/failed update reconciliation (§25 · §26);
 *   • successful/failed delete reconciliation (§27 · §28);
 *   • draft submitted-signature (start_time + end_date + end_time) +
 *     pre-submit ID set (§29 · §30) with new-ID + exact-signature
 *     match (§31 · §35), successful create removes draft (§32),
 *     failed create retains draft (§34);
 *   • `cancelEditVersion` behavior (§41–§46);
 *   • close/reset hardening for all lifecycle metadata (§47 · §48).
 *
 * Non-goals (still deferred):
 *   • NO integration into ScheduleGrid or WorkforceSchedule (owned
 *     by future 12I-2);
 *   • NO API, NO endpoint strings, NO network client;
 *   • NO frontend interval-collision calculation;
 *   • NO Day Entry writes; no other MiseOne feature blocks are
 *     touched by this component.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    parseQuickShiftInput,
    normalizeShiftIntervalInput,
    formatYMDDate,
} from "@/components/workforce/workforceHelpers";

// Prompt 18B2-P4 §6 · §7 · §10 — thin wrapper: pre-normalize friendly
// manager syntax ("20.20-22.30", "2020-2230", etc.) to canonical
// "HH:MM-HH:MM" and defer to the strict Shift parser. Same 10-minute
// planning validation is still enforced by `parseQuickShiftInput`.
function _parseFriendlyShiftInput(raw, workDate) {
    const normalized = normalizeShiftIntervalInput(raw) || raw;
    return parseQuickShiftInput(normalized, workDate);
}

// 12I-1A §19 · §31 — exact manager-facing guidance for invalid
// multi-editor input. Do NOT expose parser internals.
export const MULTI_SHIFT_INVALID_GUIDANCE =
    "Use times like 08:00 and 16:00 in 10-minute steps.";

// 12I-1B §11 — exact local Saving indicator text.
export const MULTI_SHIFT_SAVING_LABEL = "Saving…";

// 12I-1A §28 — local-only component identifier for the draft row.
// Never sent externally, never persisted, never treated as
// schedule_entry_id.
const DRAFT_LOCAL_ID = "__multi_shift_editor_local_draft__";

// Module-level stable reference so the canonical-refresh effect
// doesn't fire on every render when `cell.shifts` is missing.
const EMPTY_SHIFTS = Object.freeze([]);

const buildRowTextFromCanonical = (shifts) => {
    const next = {};
    for (const s of shifts) {
        if (s && typeof s.schedule_entry_id === "string") {
            next[s.schedule_entry_id] = {
                start: typeof s.start_time === "string" ? s.start_time : "",
                end: typeof s.end_time === "string" ? s.end_time : "",
            };
        }
    }
    return next;
};

export function MultiShiftEditor({
    open,
    onOpenChange,
    trigger,
    employee,
    cell,
    cancelEditVersion,
    isPending,
    onCommitIntent,
    onDeleteIntent,
}) {
    // 12I-1A §7 — employee data used (only first/last name).
    const firstName =
        employee && typeof employee.first_name === "string"
            ? employee.first_name
            : "";
    const lastName =
        employee && typeof employee.last_name === "string"
            ? employee.last_name
            : "";
    const fullName = `${firstName} ${lastName}`.trim();

    // 12I-1A §8 — cell shape. Day Entries are not editable here.
    const applicable = cell && cell.applicable === true;
    const canonicalShifts =
        cell && Array.isArray(cell.shifts) ? cell.shifts : EMPTY_SHIFTS;

    // 12I-1A §14 — local per-row Start/End text keyed by
    // schedule_entry_id. Draft row uses DRAFT_LOCAL_ID.
    const [rowText, setRowText] = useState({});
    // 12I-1A §25 · §26 — presence of ONE local draft.
    const [hasDraft, setHasDraft] = useState(false);
    // 12I-1B §9 · §10 · §20 — the LOCAL row that most recently emitted
    // a mutation intent. Drives Saving… placement (§11).
    // Shape: { kind: "existing" | "draft", key: string } | null.
    const [activeRow, setActiveRow] = useState(null);

    // Open-transition tracking. Initial `false` guarantees the very
    // first mount with open=true triggers rehydrate (12I-1A §33/§34).
    const prevOpenRef = useRef(false);
    // 12I-1B §41 · §42 — cancelEditVersion baseline. First observed
    // value is baseline; subsequent CHANGE means cancel/reset.
    const cancelSeenRef = useRef(cancelEditVersion);
    // 12I-1B §18 · §19 — pending true→false lifecycle tracking. We
    // must actually observe pending become true before treating a
    // later `false` as completion of a locally-emitted mutation.
    const prevIsPendingRef = useRef(false);
    const pendingObservedRef = useRef(false);
    // 12I-1B §29 · §30 — draft submission metadata used for §31/§35
    // exact new-canonical match. NEVER emitted externally.
    const submittedSignatureRef = useRef(null); // { start_time, end_date, end_time }
    const preSubmitIdsRef = useRef(null); // Set<string>

    const clearLifecycle = () => {
        setActiveRow(null);
        pendingObservedRef.current = false;
        submittedSignatureRef.current = null;
        preSubmitIdsRef.current = null;
    };

    const resetLocalToCanonical = () => {
        setRowText(buildRowTextFromCanonical(canonicalShifts));
        setHasDraft(false);
        clearLifecycle();
    };

    // 12I-1A §33 · §34 — open transitions drive local reset.
    useEffect(() => {
        const wasOpen = prevOpenRef.current;
        const nowOpen = !!open;
        if (wasOpen !== nowOpen) {
            // Both directions reset: on close discard local edits, on
            // reopen rehydrate from current canonical props (§47/§48).
            resetLocalToCanonical();
        }
        prevOpenRef.current = nowOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 12I-1B §41 · §42 · §43 · §44 · §45 · §46 — cancelEditVersion.
    // Initial mount MUST NOT be treated as cancellation. Later change
    // resets local state and (if open) requests close. Emits no
    // commit / delete intent.
    useEffect(() => {
        if (cancelSeenRef.current === cancelEditVersion) return;
        cancelSeenRef.current = cancelEditVersion;
        resetLocalToCanonical();
        if (open && typeof onOpenChange === "function") {
            onOpenChange(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cancelEditVersion]);

    // 12I-1B §18 · §19 · §20 · §25 · §26 · §27 · §28 · §31 · §32 ·
    // §34 — pending lifecycle + reconciliation. Only a true→false
    // transition (with a prior observed true) triggers reconciliation.
    useEffect(() => {
        const wasPending = prevIsPendingRef.current;
        const nowPending = !!isPending;
        prevIsPendingRef.current = nowPending;

        if (!wasPending && nowPending) {
            pendingObservedRef.current = true;
            return;
        }
        if (wasPending && !nowPending && pendingObservedRef.current) {
            // 12I-1B §19 — reconcile based on latest canonical props.
            const ar = activeRow;
            if (ar && ar.kind === "draft") {
                const sig = submittedSignatureRef.current;
                const preIds = preSubmitIdsRef.current;
                if (sig && preIds instanceof Set) {
                    // 12I-1B §31 · §35 — exact new-ID + exact signature.
                    const match = canonicalShifts.find(
                        (s) => s
                            && typeof s.schedule_entry_id === "string"
                            && !preIds.has(s.schedule_entry_id)
                            && s.start_time === sig.start_time
                            && s.end_date === sig.end_date
                            && s.end_time === sig.end_time,
                    );
                    if (match) {
                        // 12I-1B §32 — successful create: remove draft.
                        setRowText((prev) => {
                            if (!prev[DRAFT_LOCAL_ID]) return prev;
                            const next = { ...prev };
                            delete next[DRAFT_LOCAL_ID];
                            return next;
                        });
                        setHasDraft(false);
                    }
                    // Else 12I-1B §34 — retain draft (Start/End kept).
                }
            }
            // 12I-1B §20 — clear active Saving marker + all lifecycle
            // metadata. Existing-row time sync is handled by the
            // canonical-refresh effect below (§22 · §24).
            clearLifecycle();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPending]);

    // 12I-1B §22 · §24 · §27 · §38 · §39 — canonical existing-row
    // refresh. When canonical `cell.shifts` changes AND we are not in
    // a pending state, sync existing persisted-row text to canonical
    // start_time / end_time. Preserves the draft. Non-canonical keys
    // (belonging to removed schedule_entry_ids) drop out here so a
    // successful delete's local text is cleaned up.
    useEffect(() => {
        if (isPending) return;
        setRowText((prev) => {
            const next = {};
            if (prev[DRAFT_LOCAL_ID]) {
                next[DRAFT_LOCAL_ID] = prev[DRAFT_LOCAL_ID];
            }
            for (const s of canonicalShifts) {
                if (s && typeof s.schedule_entry_id === "string") {
                    next[s.schedule_entry_id] = {
                        start: typeof s.start_time === "string"
                            ? s.start_time
                            : "",
                        end: typeof s.end_time === "string"
                            ? s.end_time
                            : "",
                    };
                }
            }
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canonicalShifts, isPending]);

    // 12I-1A §9 — controlled Popover open-state change forwarded.
    const handleOpenChange = (nextOpen) => {
        if (typeof onOpenChange === "function") {
            onOpenChange(nextOpen);
        }
    };

    // 12I-1A §35 — Close action requests controlled close.
    const handleCloseClick = () => {
        if (typeof onOpenChange === "function") {
            onOpenChange(false);
        }
    };

    const getRow = (key) => rowText[key] || { start: "", end: "" };

    const setRowField = (key, field, value) => {
        setRowText((prev) => {
            const cur = prev[key] || { start: "", end: "" };
            return { ...prev, [key]: { ...cur, [field]: value } };
        });
    };

    const showInvalid = () => {
        if (toast && typeof toast.error === "function") {
            toast.error(i18n.t("wf.MultiShiftEditor.MULTI_SHIFT_INVALID_GUIDANCE", MULTI_SHIFT_INVALID_GUIDANCE));
        }
    };

    // 12I-1A §20 — Save disabled when local text equals canonical.
    const isExistingUnchanged = (shift) => {
        const { start, end } = getRow(shift.schedule_entry_id);
        return start === shift.start_time && end === shift.end_time;
    };

    const pending = !!isPending;

    // 12I-1A §17 · §20 · §21 + 12I-1B §10 · §15 — existing-row Save.
    const handleExistingSave = (shift) => {
        // 12I-1B §15 — mutation actions locked while pending.
        if (pending) return;
        const key = shift.schedule_entry_id;
        const { start, end } = getRow(key);
        const startTrim = typeof start === "string" ? start.trim() : "";
        const endTrim = typeof end === "string" ? end.trim() : "";
        // 12I-1A §20 — unchanged existing Shift never commits.
        if (startTrim === shift.start_time && endTrim === shift.end_time) {
            return;
        }
        const parsed = _parseFriendlyShiftInput(
            `${startTrim}-${endTrim}`,
            cell.date,
        );
        if (parsed === null) {
            showInvalid();
            return;
        }
        // 12I-1B §10 — record active row IMMEDIATELY (before parent
        // may asynchronously flip isPending).
        setActiveRow({ kind: "existing", key });
        pendingObservedRef.current = false;
        submittedSignatureRef.current = null;
        preSubmitIdsRef.current = null;
        if (typeof onCommitIntent === "function") {
            onCommitIntent({
                mode: "update",
                parsed,
                // 12I-1B §23 — pass the LATEST canonical Shift ref so
                // parent uses latest revision. `shift` is the row's
                // canonical object from this render's map().
                shift,
                source: "multi-editor",
            });
        }
    };

    // 12I-1A §23 + 12I-1B §10 · §15 — targeted delete intent.
    const handleExistingDelete = (shift) => {
        if (pending) return;
        setActiveRow({
            kind: "existing",
            key: shift.schedule_entry_id,
        });
        pendingObservedRef.current = false;
        submittedSignatureRef.current = null;
        preSubmitIdsRef.current = null;
        if (typeof onDeleteIntent === "function") {
            onDeleteIntent({ shift });
        }
    };

    // 12I-1A §25 + 12I-1B §15 — + Add shift click.
    const handleAddShift = () => {
        if (pending) return;
        if (hasDraft) return;
        setRowText((prev) => ({
            ...prev,
            [DRAFT_LOCAL_ID]: { start: "", end: "" },
        }));
        setHasDraft(true);
    };

    // 12I-1A §29 + 12I-1B §15 — Discard draft (no intent).
    const handleDraftDiscard = () => {
        if (pending) return;
        setRowText((prev) => {
            const next = { ...prev };
            delete next[DRAFT_LOCAL_ID];
            return next;
        });
        setHasDraft(false);
    };

    // 12I-1A §30 · §31 + 12I-1B §10 · §29 · §30 — draft Save.
    const handleDraftSave = () => {
        if (pending) return;
        const { start, end } = getRow(DRAFT_LOCAL_ID);
        const startTrim = typeof start === "string" ? start.trim() : "";
        const endTrim = typeof end === "string" ? end.trim() : "";
        const parsed = _parseFriendlyShiftInput(
            `${startTrim}-${endTrim}`,
            cell.date,
        );
        if (parsed === null) {
            showInvalid();
            return;
        }
        // 12I-1B §10 — record active row + §29/§30 signature +
        // pre-submit canonical schedule_entry_id set.
        setActiveRow({ kind: "draft", key: DRAFT_LOCAL_ID });
        pendingObservedRef.current = false;
        submittedSignatureRef.current = {
            start_time: parsed.start_time,
            end_date: parsed.end_date,
            end_time: parsed.end_time,
        };
        const preIds = new Set();
        for (const s of canonicalShifts) {
            if (s && typeof s.schedule_entry_id === "string") {
                preIds.add(s.schedule_entry_id);
            }
        }
        preSubmitIdsRef.current = preIds;
        if (typeof onCommitIntent === "function") {
            onCommitIntent({
                mode: "create",
                parsed,
                shift: null,
                source: "multi-editor",
            });
        }
    };

    // 12I-1B §11 — Saving… visibility gates.
    const isSaving = (kind, key) =>
        pending && activeRow && activeRow.kind === kind
        && activeRow.key === key;

    return (
        <Popover open={!!open} onOpenChange={handleOpenChange}>
            {/* 12I-1A §10 — trigger rendered exactly once. */}
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent
                data-testid="multi-shift-editor-content"
                className="w-96"
                align="start"
            >
                {/* 12I-1A §11 — header. No global Saving… header. */}
                <div className="mb-3">
                    <div
                        className="text-sm font-semibold"
                        data-testid="multi-shift-editor-title"
                    >
                        {i18n.t("wf.multiShiftEditor.manageShifts", "Manage shifts")}
                    </div>
                    <div
                        className="text-sm text-muted-foreground"
                        data-testid="multi-shift-editor-employee"
                    >
                        {fullName}
                    </div>
                    <div
                        className="text-xs text-muted-foreground"
                        data-testid="multi-shift-editor-date"
                    >
                        {cell && cell.date
                            ? formatYMDDate(cell.date)
                            : ""}
                    </div>
                </div>

                {/* 12I-1A §37 + 12I-1B §49 — N/A: no rows, no + Add shift. */}
                {applicable && (
                    <div data-testid="multi-shift-editor-rows">
                        {canonicalShifts.map((shift) => {
                            const key = shift.schedule_entry_id;
                            const { start, end } = getRow(key);
                            const unchanged = isExistingUnchanged(shift);
                            const rowSaving = isSaving("existing", key);
                            return (
                                <div
                                    key={key}
                                    data-testid={`multi-shift-editor-row-${key}`}
                                    data-multi-shift-row-id={key}
                                    className="mb-2 flex items-center gap-2"
                                >
                                    <Input
                                        value={start}
                                        onChange={(e) =>
                                            setRowField(
                                                key, "start", e.target.value,
                                            )
                                        }
                                        aria-label={i18n.t("wf.multiShiftEditor.start", "Start")}
                                        data-testid={`multi-shift-editor-start-${key}`}
                                    />
                                    <Input
                                        value={end}
                                        onChange={(e) =>
                                            setRowField(
                                                key, "end", e.target.value,
                                            )
                                        }
                                        aria-label={i18n.t("wf.multiShiftEditor.end", "End")}
                                        data-testid={`multi-shift-editor-end-${key}`}
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => handleExistingSave(shift)}
                                        disabled={unchanged || pending}
                                        data-testid={`multi-shift-editor-save-${key}`}
                                    >
                                        {i18n.t("common.save", "Save")}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => handleExistingDelete(shift)}
                                        disabled={pending}
                                        aria-label={i18n.t("wf.multiShiftEditor.deleteShift", "Delete shift")}
                                        data-testid={`multi-shift-editor-delete-${key}`}
                                    >
                                        {i18n.t("common.delete", "Delete")}
                                    </Button>
                                    {rowSaving && (
                                        <span
                                            className="text-xs text-muted-foreground"
                                            data-testid={`multi-shift-editor-saving-${key}`}
                                        >
                                            {MULTI_SHIFT_SAVING_LABEL}
                                        </span>
                                    )}
                                </div>
                            );
                        })}

                        {hasDraft && (
                            <div
                                data-testid="multi-shift-editor-draft-row"
                                className="mb-2 flex items-center gap-2"
                            >
                                <Input
                                    value={getRow(DRAFT_LOCAL_ID).start}
                                    onChange={(e) =>
                                        setRowField(
                                            DRAFT_LOCAL_ID,
                                            "start",
                                            e.target.value,
                                        )
                                    }
                                    aria-label={i18n.t("wf.multiShiftEditor.start", "Start")}
                                    data-testid="multi-shift-editor-draft-start"
                                />
                                <Input
                                    value={getRow(DRAFT_LOCAL_ID).end}
                                    onChange={(e) =>
                                        setRowField(
                                            DRAFT_LOCAL_ID,
                                            "end",
                                            e.target.value,
                                        )
                                    }
                                    aria-label={i18n.t("wf.multiShiftEditor.end", "End")}
                                    data-testid="multi-shift-editor-draft-end"
                                />
                                <Button
                                    type="button"
                                    onClick={handleDraftSave}
                                    disabled={pending}
                                    data-testid="multi-shift-editor-draft-save"
                                >
                                    {i18n.t("common.save", "Save")}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleDraftDiscard}
                                    disabled={pending}
                                    data-testid="multi-shift-editor-draft-discard"
                                >
                                    {i18n.t("wf.multiShiftEditor.discard", "Discard")}
                                </Button>
                                {isSaving("draft", DRAFT_LOCAL_ID) && (
                                    <span
                                        className="text-xs text-muted-foreground"
                                        data-testid="multi-shift-editor-saving-draft"
                                    >
                                        {MULTI_SHIFT_SAVING_LABEL}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* 12I-1A §25/§26 + 12I-1B §15 — Add disabled
                            while a draft exists OR while pending. */}
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={handleAddShift}
                            disabled={hasDraft || pending}
                            data-testid="multi-shift-editor-add-shift"
                        >
                            + Add shift
                        </Button>
                    </div>
                )}

                {/* 12I-1A §35 + 12I-1B §17 — Close remains available. */}
                <div className="mt-3 flex justify-end">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={handleCloseClick}
                        aria-label={i18n.t("wf.multiShiftEditor.closeShiftEditor", "Close shift editor")}
                        data-testid="multi-shift-editor-close"
                    >
                        {i18n.t("common.close", "Close")}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default MultiShiftEditor;
